
import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  Type,
  ViewContainerRef,
  DOCUMENT
} from "@angular/core";
import { Observable, Subject } from "rxjs";

export interface ModalRef<T> {
  close: (value?: T) => void;
  afterClosed: Observable<any>;
  componentRef?: ComponentRef<T>;
}

export const EVC_MODAL_REF = new InjectionToken<ModalRef<any>>("EVC_MODAL_REF");
export const EVC_MODAL_DATA = new InjectionToken<any>("EVC_MODAL_DATA");

export class ModalRefInstance<T> implements ModalRef<T> {
  afterClosed$ = new Subject<any>();
  afterClosed: Observable<any> = this.afterClosed$.asObservable();

  constructor(
    public componentRef?: ComponentRef<any>,
    private applicationRef?: ApplicationRef
  ) {}

  close(value?: any) {
    if (this.componentRef) {
      this.componentRef.destroy();
      if (this.applicationRef) {
        this.applicationRef.detachView(this.componentRef.hostView);
      }
      this.afterClosed$.next(value);
      this.afterClosed$.complete();
    }
  }
}
@Injectable({
  providedIn: "root"
})
export class EvcModalService {
  constructor(
    private injector: Injector,
    private applicationRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector,
    @Inject(DOCUMENT) private document: Document
  ) {}

  public showModal<T>(component: Type<T>, data?: any, container?: ViewContainerRef): ModalRef<T> {
    if (container && "createComponent" in container) {
      const { modalInstance, injector } = this.createInstanceAndInjector(data);

      modalInstance.componentRef = container.createComponent(component, {
        injector
      });
      return modalInstance;
    }
    return this.showRootModal(component, data);
  }

  private showRootModal<T, U>(component: Type<T>, data?: any): ModalRef<U> {
    const { modalInstance, injector } = this.createInstanceAndInjector(data, this.applicationRef);

    const componentRef = createComponent(component, {
      environmentInjector: this.environmentInjector,
      elementInjector: injector
    });

    this.applicationRef.attachView(componentRef.hostView);

    const element = (componentRef.hostView as any).rootNodes[0] as HTMLElement;
    this.document.body.appendChild(element);

    modalInstance.componentRef = componentRef;

    return modalInstance;
  }

  private createInstanceAndInjector(data: any, appRef?: ApplicationRef) {
    const modalInstance = new ModalRefInstance(null, appRef);
    const providers = [{ provide: EVC_MODAL_REF, useValue: modalInstance }];
    if (data) {
      providers.push({ provide: EVC_MODAL_DATA, useValue: data });
    }
    const injector = Injector.create({
      providers,
      parent: this.injector
    });
    return { modalInstance, injector };
  }
}
